import { AttendanceAuditLogDto } from '../../../api/scheduler/models/attendance-audit-log-dto';
import { AttendanceAuditLogListResponse } from '../../../api/scheduler/models/attendance-audit-log-list-response';
import { BaseOptionDto } from '../../../api/scheduler/models/base-option-dto';
import { ExportAttendanceAuditLogs$Params } from '../../../api/scheduler/fn/attendance-audit-log/export-attendance-audit-logs';
import { ListAttendanceAuditLogs$Params } from '../../../api/scheduler/fn/attendance-audit-log/list-attendance-audit-logs';
import {
  AttendanceLogBase,
  AttendanceLogEntry,
  AttendanceLogPage,
  AttendanceLogQuery,
  EMPTY_ATTENDANCE_LOG_PAGE,
} from '../models';
import { initialsOf } from './attendance-log-format.util';
import { toShiftParams } from './attendance-shift-filter.util';

/** An empty array would send no values anyway; leaving it out keeps the URL clean. */
function nonEmpty<T>(values: T[]): T[] | undefined {
  return values.length ? values : undefined;
}

/** Filter, search and sort parameters shared by the list and export endpoints. */
export function toExportParams(
  query: AttendanceLogQuery
): Omit<ExportAttendanceAuditLogs$Params, 'timeZone'> {
  const { filters } = query;
  const search = query.search.trim();
  return {
    date: query.date,
    search: search || undefined,
    wingType: nonEmpty(filters.wings),
    baseCode: nonEmpty(filters.baseCodes),
    rotation: nonEmpty(filters.rotations),
    ...toShiftParams(filters.shifts),
    clockInStatus: nonEmpty(filters.clockIn),
    clockOutStatus: nonEmpty(filters.clockOut),
    sort: query.sortKey ? `${query.sortKey},${query.sortDir}` : undefined,
  };
}

export function toListParams(
  query: AttendanceLogQuery
): Omit<ListAttendanceAuditLogs$Params, 'timeZone'> {
  return { ...toExportParams(query), page: query.page, size: query.size };
}

export function toAttendanceLogEntry(dto: AttendanceAuditLogDto): AttendanceLogEntry {
  return {
    id: dto.crewId,
    crewName: dto.name,
    employeeCode: dto.crewIdDisplay,
    initials: initialsOf(dto.name),
    base: dto.base ?? null,
    wing: dto.wingType,
    rotation: dto.rotation,
    shiftName: dto.shift?.name ?? null,
    attendanceAvailable: dto.attendanceAvailable,
    clockInAt: dto.clockInAt,
    clockInStatus: dto.clockInStatus,
    delayInSeconds: dto.delayInSeconds,
    clockOutAt: dto.clockOutAt,
    clockOutStatus: dto.clockOutStatus,
    delayOutSeconds: dto.delayOutSeconds,
  };
}

export function toAttendanceLogPage(response: AttendanceAuditLogListResponse): AttendanceLogPage {
  if (!response.data) {
    return EMPTY_ATTENDANCE_LOG_PAGE;
  }
  return {
    content: response.data.map(toAttendanceLogEntry),
    totalElements: response.page?.totalElements ?? response.data.length,
    totalPages: response.page?.totalPages ?? 1,
  };
}

export function toAttendanceLogBase(dto: BaseOptionDto): AttendanceLogBase {
  return { code: dto.code, displayCode: dto.displayCode, name: dto.name };
}
